import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription, AlertIcon, Box, Button, Grid, GridItem, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { useTesterEnvironment } from "./config/useTesterDefaults";
import { useTesterPrefs } from "./config/testerConfigStore";
import type { TesterConfig } from "./config/types";
import ConfigSummaryCard from "./config/ConfigSummaryCard";
import EditConfigModal from "./config/EditConfigModal";
import { useFlowRunner } from "./flow/engine/useFlowRunner";
import { DEFAULT_TARGET_ID, HAPPY_PATH, getValidActions, stepById, stepsUntil } from "./flow/flowGraph";
import { estimateSteps } from "./flow/estimateTime";
import type { FlowContext, SupplierContext } from "./flow/types";
import TargetSelector from "./components/TargetSelector";
import ExecuteBar from "./components/ExecuteBar";
import FlowTimeline from "./components/FlowTimeline";
import DecisionPrompt from "./components/DecisionPrompt";
import CurrentStateCard from "./components/CurrentStateCard";
import ExecutionLog from "./components/ExecutionLog";
import ResultSummary from "./components/ResultSummary";
import BranchActions from "./components/BranchActions";
import PlannedStepsPreview from "./components/PlannedStepsPreview";
import ExecutionHistory from "./components/ExecutionHistory";
import SupplierSessionPanel from "./components/SupplierSessionPanel";
import { EnvironmentBadge, EnvironmentGuard, getEnvInfo, isEnvConfirmed } from "./components/EnvironmentBadge";
import useNotification from "../../utils/useNotification";

const readOrcaUser = (): FlowContext["orcaUser"] => {
  try {
    const raw = JSON.parse(localStorage.getItem("ORCA_USER") || "{}");
    return {
      _id: String(raw?._id || ""),
      name: [raw?.firstName, raw?.lastName].filter(Boolean).join(" ") || "OrCA user",
      mobileNumber: String(raw?.mobileNumber || ""),
    };
  } catch {
    return { _id: "", name: "OrCA user", mobileNumber: "" };
  }
};

const OrderFlowTester: React.FC = () => {
  const toast = useNotification();
  const envQuery = useTesterEnvironment();
  const prefs = useTesterPrefs();
  const runner = useFlowRunner();
  const { state, isBusy } = runner;

  const [editOpen, setEditOpen] = useState(false);
  const [targetId, setTargetId] = useState<string>(prefs.lastTargetId || DEFAULT_TARGET_ID);
  const [logOpenKey, setLogOpenKey] = useState(0);
  const [envInfo, setEnvInfo] = useState(getEnvInfo);
  const [envConfirmed, setEnvConfirmed] = useState(() => isEnvConfirmed(getEnvInfo()));

  const env = envQuery.data;
  const config: TesterConfig | undefined = prefs.config ?? env?.defaults;
  const orcaUser = useMemo(readOrcaUser, []);

  const supplier: SupplierContext | undefined = useMemo(() => {
    if (!config?.supplierId) return undefined;
    return {
      id: config.supplierId,
      name: config.supplierName || config.supplierId,
      mobileNumber: config.supplierMobile,
      isLeanFlow: !(env?.autonomousSupplierIds || []).includes(config.supplierId),
      isTestSupplier: config.isTestSupplier !== false,
    };
  }, [config, env]);

  const previewCtx = useMemo(
    () => (config && supplier ? ({ config, supplier, orcaUser, flags: {} } as FlowContext) : undefined),
    [config, supplier, orcaUser]
  );

  const estimate = useMemo(() => {
    if (state.status === "idle") return estimateSteps(stepsUntil(targetId), previewCtx);
    const remaining = state.plannedStepIds.map((id) => stepById(id)!).filter(Boolean);
    return estimateSteps(remaining, state.ctx ?? previewCtx, state.runs);
  }, [state.status, state.plannedStepIds, state.ctx, state.runs, targetId, previewCtx]);

  const blockers = useMemo(() => {
    const list: string[] = [];
    if (!config) return ["Loading defaults…"];
    if (!config.customerId) list.push("Select a customer");
    if (!config.supplierId) list.push("Select a test supplier");
    else if (config.isTestSupplier === false) list.push("Supplier is not flagged isTestSupplier");
    if (!config.parts.length) list.push("Add at least one part");
    if (!config.shipmentAddress) list.push("Shipment address missing");
    if (!orcaUser._id) list.push("OrCA user not found in session; log in again");
    if (!envConfirmed) list.push("Confirm the backend environment below");
    return list;
  }, [config, orcaUser, envConfirmed]);

  useEffect(() => {
    setEnvInfo(getEnvInfo());
  }, []);

  const execute = useCallback(() => {
    if (!config || !supplier || blockers.length) return;
    prefs.setLastTarget(targetId);
    runner.start({
      config,
      supplier,
      orcaUser,
      targetStepId: targetId,
      supplierLoginSecret: env?.supplierLoginSecret ?? "",
    });
  }, [config, supplier, blockers, prefs, targetId, runner, orcaUser, env?.supplierLoginSecret]);

  const restart = useCallback(() => {
    runner.reset();
    setTimeout(() => execute(), 0);
  }, [runner, execute]);

  const viewOrder = useCallback(
    (orderNo?: string) => {
      const no = orderNo || state.snapshot?.child?.orderNo || state.snapshot?.enquiry?.orderNo;
      if (!no) return toast({ status: "warning", description: "No order created yet" });
      window.open(`/orders/${encodeURIComponent(no)}`, "_blank", "noopener");
    },
    [state.snapshot, toast]
  );

  const saveConfig = (next: TesterConfig) => {
    prefs.setConfig(next);
    toast({ status: "success", description: "Test configuration saved" });
  };

  const resetPrefs = () => {
    prefs.reset();
    setTargetId(DEFAULT_TARGET_ID);
    toast({ status: "info", description: "Preferences reset to defaults from mobile config" });
  };

  const branchActions = useMemo(() => getValidActions(state.snapshot, state.ctx), [state.snapshot, state.ctx]);
  const completedIds = useMemo(
    () => Object.values(state.runs).filter((r) => r.status === "completed" || r.status === "skipped").map((r) => r.stepId),
    [state.runs]
  );
  const targetIndex = HAPPY_PATH.findIndex((s) => s.id === state.targetStepId);
  const TERMINAL_STATUSES = ["CANCELLED", "DELIVERED", "RETURNED", "REJECTED"];
  const orderIsTerminal =
    TERMINAL_STATUSES.includes(state.snapshot?.enquiry?.orderStatus || "") ||
    TERMINAL_STATUSES.includes(state.snapshot?.child?.orderStatus || "");
  const canContinue =
    state.status === "completed" &&
    state.targetReached &&
    !orderIsTerminal &&
    targetIndex >= 0 &&
    targetIndex < HAPPY_PATH.length - 1;
  const [continueTargetId, setContinueTargetId] = useState<string>(DEFAULT_TARGET_ID);

  const targetLabel = stepById(targetId)?.targetLabel || "";

  if (envQuery.isLoading) {
    return (
      <HStack p={10} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.600">
          Loading mobile config, test suppliers and prefill defaults…
        </Text>
      </HStack>
    );
  }

  if (envQuery.isError || !config) {
    return (
      <Alert status="error" m={4} borderRadius="md">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Could not load tester defaults: {(envQuery.error as any)?.message || "unknown error"}.{" "}
          <Button size="xs" ml={2} onClick={() => envQuery.refetch()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box p={4} maxW="1600px" mx="auto">
      <HStack justify="space-between" mb={3} align="flex-start">
        <Box>
          <HStack>
            <Text fontSize="lg" fontWeight="800" color="gray.800">
              End-to-end order flow tester
            </Text>
            <EnvironmentBadge env={envInfo} />
          </HStack>
          <Text fontSize="xs" color="gray.500">
            Executes the real Garaaz order lifecycle through the same OrCA, customer and supplier APIs, waiting on backend events with live state verification. A non-test customer places a normal order.
          </Text>
        </Box>
        <Text fontSize="xs" color="gray.400" fontFamily="mono">
          {orcaUser.name} · {orcaUser.mobileNumber}
        </Text>
      </HStack>

      {env?.warnings.length ? (
        <Alert status="warning" borderRadius="md" mb={3} fontSize="xs" py={2}>
          <AlertIcon boxSize={4} />
          <AlertDescription>{env.warnings.join(" ")}</AlertDescription>
        </Alert>
      ) : null}

      {!envConfirmed && (
        <Box mb={3}>
          <EnvironmentGuard env={envInfo} onConfirmed={() => setEnvConfirmed(true)} />
        </Box>
      )}

      <Grid templateColumns={{ base: "1fr", xl: "minmax(0, 1fr) 360px" }} gap={4}>
        <GridItem minW={0}>
          <VStack align="stretch" spacing={3}>
            <ConfigSummaryCard
              config={config}
              isLeanSupplier={Boolean(supplier?.isLeanFlow)}
              isCustomized={Boolean(prefs.config)}
              disabled={isBusy || state.status !== "idle"}
              onEdit={() => setEditOpen(true)}
              onReset={resetPrefs}
            />

            <ExecuteBar
              state={state}
              isBusy={isBusy}
              canExecute={blockers.length === 0}
              blockers={blockers}
              estimate={estimate}
              targetLabel={targetLabel}
              onExecute={execute}
              onCancel={runner.cancel}
              onReset={runner.reset}
            />

            <AnimatePresence>
              {state.decision && <DecisionPrompt key="decision" decision={state.decision} onResolve={runner.resolveDecision} />}
            </AnimatePresence>

            <ResultSummary
              state={state}
              onViewOrder={viewOrder}
              onViewLog={() => setLogOpenKey((k) => k + 1)}
              onRetry={runner.retryStep}
              onRestart={restart}
              onNewRun={runner.reset}
            />

            {canContinue && (
              <Box bg="white" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="600">
                    Continue this order to a later target
                  </Text>
                  <HStack>
                    <select
                      value={continueTargetId}
                      onChange={(e) => setContinueTargetId(e.target.value)}
                      style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #CBD5E0", borderRadius: 6 }}
                    >
                      {HAPPY_PATH.slice(targetIndex + 1).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.targetLabel}
                        </option>
                      ))}
                    </select>
                    <Button size="xs" colorScheme="blue" onClick={() => runner.continueToTarget(continueTargetId)}>
                      Continue
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            )}

            {state.status !== "idle" ? (
              <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="700" fontSize="sm">
                    Timeline
                  </Text>
                  <Text fontSize="10px" color="gray.400">
                    click a step for API calls, cURL, polling and errors
                  </Text>
                </HStack>
                <FlowTimeline state={state} onRetry={runner.retryStep} onSkip={runner.skipFailedStep} onRestart={restart} onViewOrder={() => viewOrder()} />
              </Box>
            ) : (
              <PlannedStepsPreview
                steps={stepsUntil(targetId)}
                targetId={targetId}
                targetLabel={targetLabel}
                ctx={previewCtx}
              />
            )}

            <ExecutionLog log={state.log} snapshot={state.snapshot} forceOpenKey={logOpenKey} />
            <ExecutionHistory onViewOrder={viewOrder} />
          </VStack>
        </GridItem>

        <GridItem>
          <VStack align="stretch" spacing={3} position={{ xl: "sticky" }} top={4}>
            {state.status === "idle" && (
              <TargetSelector value={targetId} onChange={setTargetId} disabled={isBusy} ctx={previewCtx} />
            )}
            <CurrentStateCard snapshot={state.snapshot} isBusy={isBusy} onRefresh={runner.refreshState} onViewOrder={viewOrder} />
            {state.status !== "idle" && (
              <BranchActions actions={branchActions} snapshot={state.snapshot} disabled={isBusy} onRun={runner.runBranch} />
            )}
            <SupplierSessionPanel
              supplierId={config.supplierId}
              supplierName={config.supplierName}
              supplierMobile={config.supplierMobile}
              orcaUserMobile={orcaUser.mobileNumber}
              supplierLoginSecret={env?.supplierLoginSecret ?? ""}
              disabled={isBusy}
            />
            {state.status !== "idle" && completedIds.length > 0 && (
              <Text fontSize="10px" color="gray.400" textAlign="center">
                {completedIds.filter((id) => state.plannedStepIds.includes(id)).length} of {state.plannedStepIds.length} planned
                steps done
                {state.branchStepIds.length > 0 && ` · ${state.branchStepIds.length} branch action(s)`}
              </Text>
            )}
          </VStack>
        </GridItem>
      </Grid>

      <EditConfigModal isOpen={editOpen} onClose={() => setEditOpen(false)} config={config} env={env} onSave={saveConfig} />
    </Box>
  );
};

export default OrderFlowTester;
